import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Tabs, Card, Tag, Button, Table, Timeline, Spin, Row, Col,
  Statistic, Progress, Dropdown, Modal, message, Input, Select, Badge, Empty,
} from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faPause, faStop, faPlay, faRocket, faEnvelope,
  faEnvelopeOpen, faMousePointer, faReply, faTriangleExclamation,
  faBullseye, faChartLine, faUsers, faWifi, faLightbulb, faClockRotateLeft,
  faRefresh, faExpand, faCircleCheck, faCircleXmark, faPaperPlane,
  faChartPie, faArrowTrendUp, faCalendarDay, faGlobe, faFilter,
  faSearch, faEllipsisVertical, faClock, faBolt, faFire,
} from '@fortawesome/free-solid-svg-icons';
import ReactECharts from 'echarts-for-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import {
  fetchCampaign,
  fetchAnalytics,
  fetchRecipients,
  fetchLiveFeed,
  fetchActivityLog,
  fetchInsights,
  changeCampaignStatus,
  changeRecipientStatus,
} from '../../../Slices/bulkOutreachSlice';

import FunnelChart from './components/FunnelChart';
import RecipientTable from './components/RecipientTable';
import LiveFeed from './components/LiveFeed';
import ActivityTimeline from './components/ActivityTimeline';
import EngagementBadge from './components/EngagementBadge';
import socket from '../Sales1/socket';

dayjs.extend(relativeTime);

// ─── Design Tokens (dark-blue glass-morphism palette) ───────────────────────

const T = {
  pageBg: '#0b1120',
  cardBg: 'rgba(15, 23, 42, 0.65)',
  cardBorder: 'rgba(99, 102, 241, 0.15)',
  cardGlow: '0 4px 24px rgba(99, 102, 241, 0.08)',
  glass: 'blur(16px) saturate(180%)',
  text: '#f1f5f9',
  textSecondary: '#94a3b8',
  textMuted: '#64748b',
  indigo: '#6366f1',
  indigoDark: '#4f46e5',
  indigoGlow: 'rgba(99, 102, 241, 0.25)',
  violet: '#8b5cf6',
  emerald: '#10b981',
  emeraldGlow: 'rgba(16, 185, 129, 0.2)',
  amber: '#f59e0b',
  amberGlow: 'rgba(245, 158, 11, 0.2)',
  rose: '#f43f5e',
  roseGlow: 'rgba(244, 63, 94, 0.2)',
  sky: '#0ea5e9',
  skyGlow: 'rgba(14, 165, 233, 0.2)',
  teal: '#14b8a6',
  cyan: '#06b6d4',
  border: 'rgba(148, 163, 184, 0.12)',
  borderHover: 'rgba(99, 102, 241, 0.35)',
  radius: '16px',
  radiusSm: '12px',
  radiusXs: '8px',
  shadow: '0 1px 3px rgba(0,0,0,0.2), 0 1px 2px rgba(0,0,0,0.3)',
  shadowLg: '0 10px 25px rgba(0,0,0,0.25)',
};

// ─── Status configuration ───────────────────────────────────────────────────

const STATUS_CONFIG = {
  draft: { color: T.textMuted, label: 'Draft', bg: 'rgba(100,116,139,0.15)' },
  scheduled: { color: T.sky, label: 'Scheduled', bg: 'rgba(14,165,233,0.15)' },
  active: { color: T.emerald, label: 'Active', bg: 'rgba(16,185,129,0.15)' },
  running: { color: T.emerald, label: 'Running', bg: 'rgba(16,185,129,0.15)' },
  paused: { color: T.amber, label: 'Paused', bg: 'rgba(245,158,11,0.15)' },
  completed: { color: T.indigo, label: 'Completed', bg: 'rgba(99,102,241,0.15)' },
  cancelled: { color: T.rose, label: 'Cancelled', bg: 'rgba(244,63,94,0.15)' },
  stopped: { color: T.rose, label: 'Stopped', bg: 'rgba(244,63,94,0.15)' },
  error: { color: T.rose, label: 'Error', bg: 'rgba(244,63,94,0.15)' },
};

// ─── Inject global styles ───────────────────────────────────────────────────

const STYLE_ID = 'campaign-detail-styles';
if (typeof document !== 'undefined' && !document.getElementById(STYLE_ID)) {
  const sheet = document.createElement('style');
  sheet.id = STYLE_ID;
  sheet.textContent = `
    @keyframes cd-fadeIn {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes cd-shimmer {
      0% { background-position: -200% center; }
      100% { background-position: 200% center; }
    }
    @keyframes cd-pulseGlow {
      0%, 100% { box-shadow: 0 0 0 0 rgba(99,102,241,0.3); }
      50% { box-shadow: 0 0 0 8px rgba(99,102,241,0); }
    }
    .cd-glass-card {
      background: ${T.cardBg};
      backdrop-filter: ${T.glass};
      -webkit-backdrop-filter: ${T.glass};
      border: 1px solid ${T.cardBorder};
      border-radius: ${T.radius};
      box-shadow: ${T.cardGlow};
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      animation: cd-fadeIn 0.4s ease-out both;
    }
    .cd-glass-card:hover {
      border-color: ${T.borderHover};
      box-shadow: 0 8px 32px rgba(99,102,241,0.12);
      transform: translateY(-2px);
    }
    .cd-metric-card {
      background: ${T.cardBg};
      backdrop-filter: ${T.glass};
      -webkit-backdrop-filter: ${T.glass};
      border: 1px solid ${T.cardBorder};
      border-radius: ${T.radius};
      padding: 20px 24px;
      position: relative;
      overflow: hidden;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      animation: cd-fadeIn 0.4s ease-out both;
    }
    .cd-metric-card::before {
      content: '';
      position: absolute;
      top: 0; left: 0; right: 0;
      height: 3px;
      background: var(--accent-gradient);
      opacity: 0;
      transition: opacity 0.3s;
    }
    .cd-metric-card:hover {
      border-color: ${T.borderHover};
      box-shadow: 0 8px 32px rgba(99,102,241,0.12);
      transform: translateY(-3px);
    }
    .cd-metric-card:hover::before { opacity: 1; }
    .cd-tabs .ant-tabs-nav { margin-bottom: 0 !important; }
    .cd-tabs .ant-tabs-nav::before { border: none !important; }
    .cd-tabs .ant-tabs-tab {
      color: ${T.textMuted} !important;
      font-weight: 600 !important;
      font-size: 13px !important;
      padding: 12px 20px !important;
      margin: 0 !important;
      border-radius: 12px 12px 0 0 !important;
      transition: all 0.2s !important;
    }
    .cd-tabs .ant-tabs-tab:hover {
      color: ${T.text} !important;
      background: rgba(99,102,241,0.06) !important;
    }
    .cd-tabs .ant-tabs-tab-active .ant-tabs-tab-btn {
      color: ${T.indigo} !important;
    }
    .cd-tabs .ant-tabs-ink-bar {
      background: linear-gradient(90deg, ${T.indigo}, ${T.violet}) !important;
      height: 3px !important;
      border-radius: 3px !important;
    }
    .cd-tabs .ant-tabs-content-holder {
      background: ${T.cardBg};
      backdrop-filter: ${T.glass};
      -webkit-backdrop-filter: ${T.glass};
      border: 1px solid ${T.cardBorder};
      border-radius: 0 0 ${T.radius} ${T.radius};
      padding: 24px;
    }
    .cd-table .ant-table {
      background: transparent !important;
      color: ${T.text} !important;
    }
    .cd-table .ant-table-thead > tr > th {
      background: rgba(15,23,42,0.8) !important;
      color: ${T.textSecondary} !important;
      border-bottom: 1px solid ${T.border} !important;
      font-weight: 600 !important;
      font-size: 11px !important;
      text-transform: uppercase !important;
      letter-spacing: 0.5px !important;
    }
    .cd-table .ant-table-tbody > tr > td {
      border-bottom: 1px solid ${T.border} !important;
      color: ${T.text} !important;
      transition: background 0.2s !important;
    }
    .cd-table .ant-table-tbody > tr:hover > td {
      background: rgba(99,102,241,0.06) !important;
    }
    .cd-table .ant-table-placeholder {
      background: transparent !important;
    }
    .cd-table .ant-empty-description {
      color: ${T.textMuted} !important;
    }
    .cd-timeline .ant-timeline-item-content {
      color: ${T.text} !important;
    }
    .cd-timeline .ant-timeline-item-tail {
      border-inline-start-color: ${T.border} !important;
    }
    .cd-status-filter {
      display: flex;
      gap: 6px;
      flex-wrap: wrap;
      margin-bottom: 16px;
    }
    .cd-status-btn {
      padding: 6px 14px;
      border-radius: 20px;
      border: 1px solid ${T.border};
      background: transparent;
      color: ${T.textSecondary};
      font-size: 12px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s;
      display: inline-flex;
      align-items: center;
      gap: 6px;
    }
    .cd-status-btn:hover {
      border-color: ${T.indigo};
      color: ${T.text};
    }
    .cd-status-btn.active {
      background: linear-gradient(135deg, ${T.indigo}, ${T.violet});
      color: #fff;
      border-color: transparent;
      box-shadow: 0 2px 8px ${T.indigoGlow};
    }
    .cd-search-input {
      background: rgba(15,23,42,0.6) !important;
      border: 1px solid ${T.border} !important;
      border-radius: 10px !important;
      color: ${T.text} !important;
    }
    .cd-search-input .ant-input {
      background: transparent !important;
      color: ${T.text} !important;
    }
    .cd-search-input .ant-input::placeholder {
      color: ${T.textMuted} !important;
    }
    .cd-search-input .ant-input-prefix {
      color: ${T.textMuted} !important;
    }
    .cd-insight-card {
      background: rgba(99,102,241,0.06);
      border: 1px solid rgba(99,102,241,0.12);
      border-radius: 14px;
      padding: 20px 24px;
      margin-bottom: 14px;
      transition: all 0.3s;
      animation: cd-fadeIn 0.4s ease-out both;
    }
    .cd-insight-card:hover {
      border-color: rgba(99,102,241,0.25);
      background: rgba(99,102,241,0.1);
      transform: translateX(4px);
    }
    .cd-live-event {
      display: flex;
      align-items: flex-start;
      gap: 14px;
      padding: 14px 18px;
      border-bottom: 1px solid ${T.border};
      animation: cd-fadeIn 0.3s ease-out both;
      transition: background 0.2s;
    }
    .cd-live-event:hover {
      background: rgba(99,102,241,0.04);
    }
    .cd-live-event:last-child {
      border-bottom: none;
    }
    .cd-btn-primary {
      background: linear-gradient(135deg, ${T.indigo}, ${T.violet});
      border: none;
      border-radius: 10px;
      color: #fff;
      font-weight: 600;
      font-size: 13px;
      padding: 8px 20px;
      cursor: pointer;
      transition: all 0.3s;
      box-shadow: 0 4px 12px ${T.indigoGlow};
      display: inline-flex;
      align-items: center;
      gap: 8px;
    }
    .cd-btn-primary:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 20px rgba(99,102,241,0.35);
    }
    .cd-btn-danger {
      background: linear-gradient(135deg, ${T.rose}, #e11d48);
      border: none;
      border-radius: 10px;
      color: #fff;
      font-weight: 600;
      font-size: 13px;
      padding: 8px 20px;
      cursor: pointer;
      transition: all 0.3s;
      box-shadow: 0 4px 12px ${T.roseGlow};
      display: inline-flex;
      align-items: center;
      gap: 8px;
    }
    .cd-btn-danger:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 20px rgba(244,63,94,0.35);
    }
    .cd-btn-warning {
      background: linear-gradient(135deg, ${T.amber}, #f97316);
      border: none;
      border-radius: 10px;
      color: #fff;
      font-weight: 600;
      font-size: 13px;
      padding: 8px 20px;
      cursor: pointer;
      transition: all 0.3s;
      box-shadow: 0 4px 12px ${T.amberGlow};
      display: inline-flex;
      align-items: center;
      gap: 8px;
    }
    .cd-btn-warning:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 20px rgba(245,158,11,0.35);
    }
    .cd-btn-ghost {
      background: transparent;
      border: 1px solid ${T.border};
      border-radius: 10px;
      color: ${T.textSecondary};
      font-weight: 600;
      font-size: 13px;
      padding: 8px 20px;
      cursor: pointer;
      transition: all 0.2s;
      display: inline-flex;
      align-items: center;
      gap: 8px;
    }
    .cd-btn-ghost:hover {
      border-color: ${T.indigo};
      color: ${T.text};
      background: rgba(99,102,241,0.06);
    }
    .cd-ab-card {
      background: rgba(139,92,246,0.06);
      border: 1px solid rgba(139,92,246,0.15);
      border-radius: 14px;
      padding: 20px;
      transition: all 0.3s;
    }
    .cd-ab-card:hover {
      border-color: rgba(139,92,246,0.3);
      transform: translateY(-2px);
    }
  `;
  document.head.appendChild(sheet);
}

// ─── ECharts dark-blue theme ────────────────────────────────────────────────

const ECHART_THEME = {
  backgroundColor: 'transparent',
  textStyle: { color: T.textSecondary, fontSize: 11 },
  title: { textStyle: { color: T.text } },
  legend: { textStyle: { color: T.textSecondary, fontSize: 11 } },
  tooltip: {
    backgroundColor: 'rgba(15,23,42,0.92)',
    borderColor: T.cardBorder,
    textStyle: { color: T.text, fontSize: 12 },
    extraCssText: 'backdrop-filter:blur(8px);border-radius:10px;box-shadow:0 8px 32px rgba(0,0,0,0.3);',
  },
  grid: {
    left: 50, right: 24, top: 40, bottom: 36,
    borderColor: T.border,
  },
  categoryAxis: {
    axisLine: { lineStyle: { color: T.border } },
    axisTick: { lineStyle: { color: T.border } },
    axisLabel: { color: T.textSecondary, fontSize: 11 },
    splitLine: { lineStyle: { color: T.border, type: 'dashed' } },
  },
  valueAxis: {
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: { color: T.textMuted, fontSize: 11 },
    splitLine: { lineStyle: { color: T.border, type: 'dashed' } },
  },
};

// ─── Metric colors for the 6 KPI cards ─────────────────────────────────────

const METRIC_DEFS = [
  { key: 'sent', label: 'Sent', icon: faPaperPlane, color: T.indigo, gradient: `linear-gradient(135deg, ${T.indigo}, ${T.violet})` },
  { key: 'delivered', label: 'Delivered', icon: faEnvelope, color: T.teal, gradient: `linear-gradient(135deg, ${T.teal}, ${T.cyan})` },
  { key: 'opened', label: 'Opened', icon: faEnvelopeOpen, color: T.sky, gradient: `linear-gradient(135deg, ${T.sky}, ${T.cyan})` },
  { key: 'clicked', label: 'Clicked', icon: faMousePointer, color: T.amber, gradient: `linear-gradient(135deg, ${T.amber}, #f97316)` },
  { key: 'replied', label: 'Replied', icon: faReply, color: T.emerald, gradient: `linear-gradient(135deg, ${T.emerald}, ${T.teal})` },
  { key: 'bounced', label: 'Bounced', icon: faTriangleExclamation, color: T.rose, gradient: `linear-gradient(135deg, ${T.rose}, #e11d48)` },
];

// ─── Recipient status filter tabs ───────────────────────────────────────────

const RECIPIENT_STATUSES = [
  { key: 'all', label: 'All' },
  { key: 'queued', label: 'Queued' },
  { key: 'sent', label: 'Sent' },
  { key: 'opened', label: 'Opened' },
  { key: 'replied', label: 'Replied' },
  { key: 'bounced', label: 'Bounced' },
  { key: 'paused', label: 'Paused' },
  { key: 'stopped', label: 'Stopped' },
];

// ─── Event type icon/color mapping ──────────────────────────────────────────

const EVENT_ICONS = {
  sent: { emoji: '\u2705', color: T.emerald, label: 'Sent' },
  delivered: { emoji: '\uD83D\uDCEC', color: T.teal, label: 'Delivered' },
  opened: { emoji: '\uD83D\uDC41', color: T.sky, label: 'Opened' },
  clicked: { emoji: '\uD83D\uDD17', color: T.amber, label: 'Clicked' },
  replied: { emoji: '\uD83D\uDCAC', color: T.emerald, label: 'Replied' },
  bounced: { emoji: '\u274C', color: T.rose, label: 'Bounced' },
  unsubscribed: { emoji: '\uD83D\uDEAB', color: T.rose, label: 'Unsubscribed' },
};

// ─── Utility: safe percentage ───────────────────────────────────────────────

const pct = (num, denom) => {
  if (!denom || denom === 0) return 0;
  return Math.round((num / denom) * 1000) / 10;
};

const fmtNum = (n) => {
  if (n == null) return '0';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
};

// ─── Glass Metric Card ──────────────────────────────────────────────────────

const MetricCard = ({ label, value, percent, sub, color, gradient, icon, delay = 0 }) => (
  <div
    className="cd-metric-card"
    style={{ '--accent-gradient': gradient, animationDelay: `${delay}ms` }}
  >
    <div style={{ position: 'absolute', top: 14, right: 18, opacity: 0.12 }}>
      <FontAwesomeIcon icon={icon} style={{ fontSize: 36, color }} />
    </div>
    <div style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
      {label}
    </div>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
      <span style={{ fontSize: 28, fontWeight: 800, color: T.text, lineHeight: 1 }}>
        {fmtNum(value)}
      </span>
      {percent != null && (
        <span style={{
          fontSize: 13, fontWeight: 700, color,
          background: `${color}18`, padding: '2px 8px', borderRadius: 8,
        }}>
          {percent}%
        </span>
      )}
    </div>
    {sub && (
      <div style={{ fontSize: 11, color: T.textMuted, marginTop: 6 }}>{sub}</div>
    )}
    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 3, background: gradient, opacity: 0.5 }} />
  </div>
);

// ─── Section Header ─────────────────────────────────────────────────────────

const SectionHeader = ({ title, icon, rightContent }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, marginTop: 24 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      {icon && <FontAwesomeIcon icon={icon} style={{ color: T.indigo, fontSize: 15 }} />}
      <span style={{ fontSize: 15, fontWeight: 700, color: T.text, letterSpacing: '-0.3px' }}>{title}</span>
    </div>
    {rightContent}
  </div>
);

// ═════════════════════════════════════════════════════════════════════════════
// CAMPAIGN DETAIL COMPONENT
// ═════════════════════════════════════════════════════════════════════════════

const CampaignDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // ── Redux state ─────────────────────────────────────────────────────────
  const {
    currentCampaign: campaign,
    analytics,
    recipients,
    recipientsTotal,
    liveFeed,
    activityLog,
    activityLogTotal,
    insights,
    loading,
    error,
  } = useSelector((state) => state.bulkOutreach);

  // ── Local state ─────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('analytics');
  const [recipientFilter, setRecipientFilter] = useState('all');
  const [recipientSearch, setRecipientSearch] = useState('');
  const [recipientPage, setRecipientPage] = useState(1);
  const [activityPage, setActivityPage] = useState(1);
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [feedLoading, setFeedLoading] = useState(false);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [activityLoading, setActivityLoading] = useState(false);

  // ── Initial data fetch ──────────────────────────────────────────────────

  useEffect(() => {
    if (id) {
      dispatch(fetchCampaign(id));
      // Auto-refresh every 30s for active campaigns
      const interval = setInterval(() => dispatch(fetchCampaign(id)), 30000);
      return () => clearInterval(interval);
    }
  }, [dispatch, id]);

  // ── Fetch analytics on mount (default tab) ─────────────────────────────

  useEffect(() => {
    if (id && activeTab === 'analytics') {
      setAnalyticsLoading(true);
      dispatch(fetchAnalytics(id)).finally(() => setAnalyticsLoading(false));
      // Auto-refresh analytics every 30s
      const interval = setInterval(() => {
        dispatch(fetchAnalytics(id));
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [dispatch, id, activeTab]);

  // ── Tab change handler ──────────────────────────────────────────────────

  const handleTabChange = useCallback((key) => {
    setActiveTab(key);
    if (!id) return;

    switch (key) {
      case 'analytics':
        setAnalyticsLoading(true);
        dispatch(fetchAnalytics(id)).finally(() => setAnalyticsLoading(false));
        break;
      case 'recipients':
        setRecipientsLoading(true);
        setRecipientPage(1);
        setRecipientFilter('all');
        setRecipientSearch('');
        dispatch(fetchRecipients({ campaignId: id, page: 1, limit: 50 }))
          .finally(() => setRecipientsLoading(false));
        break;
      case 'livefeed':
        setFeedLoading(true);
        dispatch(fetchLiveFeed({ campaignId: id, limit: 50 }))
          .finally(() => setFeedLoading(false));
        break;
      case 'insights':
        setInsightsLoading(true);
        dispatch(fetchInsights(id)).finally(() => setInsightsLoading(false));
        break;
      case 'activity':
        setActivityLoading(true);
        setActivityPage(1);
        dispatch(fetchActivityLog({ campaignId: id, page: 1, limit: 50 }))
          .finally(() => setActivityLoading(false));
        break;
      default:
        break;
    }
  }, [dispatch, id]);

  // ── Recipient filter / search / pagination ──────────────────────────────

  useEffect(() => {
    if (activeTab !== 'recipients' || !id) return;
    setRecipientsLoading(true);
    dispatch(fetchRecipients({
      campaignId: id,
      page: recipientPage,
      limit: 50,
      status: recipientFilter === 'all' ? undefined : recipientFilter,
      search: recipientSearch || undefined,
    })).finally(() => setRecipientsLoading(false));
  }, [dispatch, id, recipientFilter, recipientSearch, recipientPage, activeTab]);

  // ── Activity log pagination ─────────────────────────────────────────────

  useEffect(() => {
    if (activeTab !== 'activity' || !id) return;
    setActivityLoading(true);
    dispatch(fetchActivityLog({ campaignId: id, page: activityPage, limit: 50 }))
      .finally(() => setActivityLoading(false));
  }, [dispatch, id, activityPage, activeTab]);

  // ── Real-time updates via socket (backend/cron emit bulkOutreach:* events) ─
  useEffect(() => {
    if (!id) return;
    const isThis = (data) => String(data?.campaignId) === String(id);
    const refreshActiveTab = () => {
      if (activeTab === 'analytics') dispatch(fetchAnalytics(id));
      else if (activeTab === 'livefeed') dispatch(fetchLiveFeed({ campaignId: id, limit: 50 }));
      else if (activeTab === 'activity') dispatch(fetchActivityLog({ campaignId: id, page: activityPage, limit: 50 }));
      else if (activeTab === 'recipients') dispatch(fetchRecipients({ campaignId: id, page: recipientPage, limit: 50, status: recipientFilter === 'all' ? undefined : recipientFilter, search: recipientSearch || undefined }));
    };
    const onStatusChanged = (d) => { if (isThis(d)) dispatch(fetchCampaign(id)); };
    const onActivity = (d) => { if (isThis(d)) { dispatch(fetchCampaign(id)); refreshActiveTab(); } };
    const onReply = (d) => { if (isThis(d)) { dispatch(fetchCampaign(id)); refreshActiveTab(); } };
    const onWinner = (d) => { if (isThis(d)) dispatch(fetchAnalytics(id)); };
    const onRecipientUpdate = (d) => { if (isThis(d)) refreshActiveTab(); };
    socket.on('bulkOutreach:statusChanged', onStatusChanged);
    socket.on('bulkOutreach:activity', onActivity);
    socket.on('bulkOutreach:reply', onReply);
    socket.on('bulkOutreach:winnerPicked', onWinner);
    socket.on('bulkOutreach:recipientUpdate', onRecipientUpdate);
    return () => {
      socket.off('bulkOutreach:statusChanged', onStatusChanged);
      socket.off('bulkOutreach:activity', onActivity);
      socket.off('bulkOutreach:reply', onReply);
      socket.off('bulkOutreach:winnerPicked', onWinner);
      socket.off('bulkOutreach:recipientUpdate', onRecipientUpdate);
    };
  }, [dispatch, id, activeTab, activityPage, recipientPage, recipientFilter, recipientSearch]);

  // ── Campaign status change ──────────────────────────────────────────────

  const handleStatusChange = useCallback((newStatus) => {
    setPendingStatus(newStatus);
    setStatusModalVisible(true);
  }, []);

  const confirmStatusChange = useCallback(() => {
    if (!id || !pendingStatus) return;
    dispatch(changeCampaignStatus({ id, status: pendingStatus }))
      .unwrap()
      .then(() => {
        const verb = { active: 'launched', resumed: 'resumed', paused: 'paused', cancelled: 'cancelled', scheduled: 'scheduled' }[pendingStatus] || 'updated';
        message.success(`Campaign ${verb} successfully`);
        setStatusModalVisible(false);
        setPendingStatus(null);
      })
      .catch((err) => {
        message.error(err || `Failed to ${pendingStatus} campaign`);
      });
  }, [dispatch, id, pendingStatus]);

  // ── Derived data ────────────────────────────────────────────────────────

  const metrics = useMemo(() => {
    if (!analytics) return null;
    const m = analytics.metrics || analytics.funnel || analytics;
    const sent = m.sent || 0;
    const delivered = m.delivered || 0;
    const opened = m.opened || 0;
    const clicked = m.clicked || 0;
    const replied = m.replied || 0;
    const bounced = m.bounced || 0;
    // Prefer the backend's authoritative bounceBreakdown; otherwise treat metrics.bounced
    // as hard bounces (that is how the cron aggregates it) and softBounced separately.
    const bb = analytics?.bounceBreakdown || {};
    const softBounce = bb.soft ?? m.softBounced ?? m.softBounce ?? 0;
    const hardBounce = bb.hard ?? bounced;

    return {
      sent: { value: sent, percent: null },
      delivered: { value: delivered, percent: pct(delivered, sent) },
      // Open/click/reply rates are measured against emails SENT (delivered is often 0 until
      // SES delivery webhooks land, which would make these read 0% incorrectly).
      opened: { value: opened, percent: pct(opened, sent) },
      clicked: { value: clicked, percent: pct(clicked, sent) },
      replied: { value: replied, percent: pct(replied, sent) },
      bounced: {
        value: bounced,
        percent: pct(bounced, sent),
        sub: `Hard: ${hardBounce} | Soft: ${softBounce}`,
      },
    };
  }, [analytics]);

  // ── ECharts: Timeline line chart (daily sent/opened/replied) ──────────

  const timelineChartOption = useMemo(() => {
    const daily = analytics?.dailyTimeline || analytics?.timeline || [];
    if (!daily.length) return null;

    const dates = daily.map((d) => dayjs(d.date).format('MMM DD'));
    const sentData = daily.map((d) => d.sent || 0);
    const openedData = daily.map((d) => d.opened || 0);
    const repliedData = daily.map((d) => d.replied || 0);

    return {
      ...ECHART_THEME,
      tooltip: {
        ...ECHART_THEME.tooltip,
        trigger: 'axis',
        axisPointer: { type: 'cross', lineStyle: { color: T.border } },
      },
      legend: {
        ...ECHART_THEME.legend,
        bottom: 0,
        data: ['Sent', 'Opened', 'Replied'],
      },
      grid: { left: 50, right: 24, top: 20, bottom: 40 },
      xAxis: {
        type: 'category',
        data: dates,
        boundaryGap: false,
        ...ECHART_THEME.categoryAxis,
      },
      yAxis: {
        type: 'value',
        ...ECHART_THEME.valueAxis,
      },
      series: [
        {
          name: 'Sent',
          type: 'line',
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: { width: 2.5, color: T.indigo },
          itemStyle: { color: T.indigo },
          areaStyle: {
            color: {
              type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: `${T.indigo}30` },
                { offset: 1, color: `${T.indigo}05` },
              ],
            },
          },
          data: sentData,
        },
        {
          name: 'Opened',
          type: 'line',
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: { width: 2.5, color: T.sky },
          itemStyle: { color: T.sky },
          areaStyle: {
            color: {
              type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: `${T.sky}30` },
                { offset: 1, color: `${T.sky}05` },
              ],
            },
          },
          data: openedData,
        },
        {
          name: 'Replied',
          type: 'line',
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: { width: 2.5, color: T.emerald },
          itemStyle: { color: T.emerald },
          areaStyle: {
            color: {
              type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: `${T.emerald}30` },
                { offset: 1, color: `${T.emerald}05` },
              ],
            },
          },
          data: repliedData,
        },
      ],
    };
  }, [analytics]);

  // ── ECharts: Per-step comparison bar chart ────────────────────────────

  const stepComparisonOption = useMemo(() => {
    const steps = analytics?.stepBreakdown || analytics?.perStep || [];
    if (!steps.length) return null;

    const labels = steps.map((s) => s.stepName || s.name || `Step ${s.stepIndex || s.step || '?'}`);
    const openRates = steps.map((s) => pct(s.opened || 0, s.delivered || s.sent || 1));
    const replyRates = steps.map((s) => pct(s.replied || 0, s.delivered || s.sent || 1));

    return {
      ...ECHART_THEME,
      tooltip: {
        ...ECHART_THEME.tooltip,
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params) => {
          let str = `<div style="font-weight:700;margin-bottom:6px;">${params[0].axisValue}</div>`;
          params.forEach((p) => {
            str += `<div style="display:flex;align-items:center;gap:6px;margin:3px 0;">
              <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${p.color}"></span>
              ${p.seriesName}: <b>${p.value}%</b>
            </div>`;
          });
          return str;
        },
      },
      legend: {
        ...ECHART_THEME.legend,
        bottom: 0,
        data: ['Open Rate', 'Reply Rate'],
      },
      grid: { left: 60, right: 24, top: 20, bottom: 40 },
      xAxis: {
        type: 'category',
        data: labels,
        ...ECHART_THEME.categoryAxis,
        axisLabel: { ...ECHART_THEME.categoryAxis.axisLabel, rotate: labels.length > 6 ? 30 : 0 },
      },
      yAxis: {
        type: 'value',
        ...ECHART_THEME.valueAxis,
        axisLabel: { ...ECHART_THEME.valueAxis.axisLabel, formatter: '{value}%' },
        max: 100,
      },
      series: [
        {
          name: 'Open Rate',
          type: 'bar',
          barWidth: 24,
          barGap: '20%',
          itemStyle: {
            color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: T.sky }, { offset: 1, color: T.cyan }] },
            borderRadius: [6, 6, 0, 0],
          },
          data: openRates,
        },
        {
          name: 'Reply Rate',
          type: 'bar',
          barWidth: 24,
          itemStyle: {
            color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: T.emerald }, { offset: 1, color: T.teal }] },
            borderRadius: [6, 6, 0, 0],
          },
          data: replyRates,
        },
      ],
    };
  }, [analytics]);

  // ── ECharts: Heatmap (opens by hour x day of week) ────────────────────

  const heatmapOption = useMemo(() => {
    const heatmapData = analytics?.heatmap || analytics?.opensByHourDay || [];
    if (!heatmapData.length) return null;

    const hours = Array.from({ length: 24 }, (_, i) => {
      const h = i % 12 || 12;
      return `${h}${i < 12 ? 'a' : 'p'}`;
    });
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    // Normalize: expecting [{hour, day, count}] or a 7x24 matrix
    let chartData;
    if (Array.isArray(heatmapData[0])) {
      // Matrix format: heatmapData[day][hour] = count
      chartData = [];
      heatmapData.forEach((row, dayIdx) => {
        row.forEach((val, hourIdx) => {
          chartData.push([hourIdx, dayIdx, val || 0]);
        });
      });
    } else {
      // Object format: [{hour, day, count}]
      chartData = heatmapData.map((d) => [d.hour, d.day, d.count || d.value || 0]);
    }

    const maxVal = Math.max(...chartData.map((d) => d[2]), 1);

    return {
      ...ECHART_THEME,
      tooltip: {
        ...ECHART_THEME.tooltip,
        formatter: (params) => {
          const [hour, day, count] = params.data;
          return `<b>${days[day]} ${hours[hour]}</b><br/>Opens: <b>${count}</b>`;
        },
      },
      grid: { left: 50, right: 24, top: 10, bottom: 40 },
      xAxis: {
        type: 'category',
        data: hours,
        splitArea: { show: true, areaStyle: { color: ['transparent', 'rgba(99,102,241,0.02)'] } },
        ...ECHART_THEME.categoryAxis,
        axisLabel: { ...ECHART_THEME.categoryAxis.axisLabel, fontSize: 10 },
      },
      yAxis: {
        type: 'category',
        data: days,
        ...ECHART_THEME.categoryAxis,
      },
      visualMap: {
        min: 0,
        max: maxVal,
        calculable: false,
        orient: 'horizontal',
        left: 'center',
        bottom: 0,
        itemWidth: 12,
        itemHeight: 120,
        textStyle: { color: T.textMuted, fontSize: 10 },
        inRange: {
          color: ['rgba(99,102,241,0.05)', 'rgba(99,102,241,0.2)', 'rgba(99,102,241,0.45)', T.indigo, T.indigoDark],
        },
      },
      series: [{
        name: 'Opens',
        type: 'heatmap',
        data: chartData,
        label: { show: false },
        itemStyle: { borderColor: 'rgba(15,23,42,0.6)', borderWidth: 2, borderRadius: 3 },
        emphasis: {
          itemStyle: { shadowBlur: 10, shadowColor: T.indigoGlow },
        },
      }],
    };
  }, [analytics]);

  // ── ECharts: Timezone distribution pie chart ──────────────────────────

  const timezoneChartOption = useMemo(() => {
    const tzData = analytics?.timezoneDistribution || analytics?.timezones || [];
    if (!tzData.length) return null;

    const colors = [T.indigo, T.sky, T.emerald, T.amber, T.violet, T.teal, T.rose, T.cyan];

    return {
      ...ECHART_THEME,
      tooltip: {
        ...ECHART_THEME.tooltip,
        trigger: 'item',
        formatter: '{b}: {c} ({d}%)',
      },
      legend: {
        ...ECHART_THEME.legend,
        type: 'scroll',
        bottom: 0,
        itemWidth: 10,
        itemHeight: 10,
      },
      series: [{
        type: 'pie',
        radius: ['40%', '70%'],
        center: ['50%', '45%'],
        itemStyle: { borderRadius: 6, borderColor: 'rgba(15,23,42,0.8)', borderWidth: 2 },
        label: { show: false },
        emphasis: { label: { show: true, fontSize: 12, fontWeight: 700, color: T.text } },
        data: tzData.map((tz, i) => ({
          name: tz.timezone || tz.name || tz.tz,
          value: tz.count || tz.value || 0,
          itemStyle: { color: colors[i % colors.length] },
        })),
      }],
    };
  }, [analytics]);

  // ── ECharts: Engagement distribution pie chart ────────────────────────

  const engagementChartOption = useMemo(() => {
    const eng = analytics?.engagementDistribution || analytics?.engagement || null;
    if (!eng) return null;

    const data = [];
    if (eng.none != null) data.push({ name: 'No Engagement', value: eng.none, itemStyle: { color: T.textMuted } });
    if (eng.engaged != null) data.push({ name: 'Engaged', value: eng.engaged, itemStyle: { color: T.sky } });
    if (eng.highlyEngaged != null || eng.highly_engaged != null) {
      data.push({ name: 'Highly Engaged', value: eng.highlyEngaged || eng.highly_engaged, itemStyle: { color: T.emerald } });
    }
    // Also handle array format
    if (!data.length && Array.isArray(eng)) {
      const engColors = { none: T.textMuted, low: T.amber, engaged: T.sky, highly_engaged: T.emerald, highlyEngaged: T.emerald };
      eng.forEach((e) => {
        data.push({
          name: e.level || e.name || e.label,
          value: e.count || e.value || 0,
          itemStyle: { color: engColors[e.level] || T.indigo },
        });
      });
    }

    if (!data.length) return null;

    return {
      ...ECHART_THEME,
      tooltip: {
        ...ECHART_THEME.tooltip,
        trigger: 'item',
        formatter: '{b}: {c} ({d}%)',
      },
      legend: {
        ...ECHART_THEME.legend,
        bottom: 0,
        itemWidth: 10,
        itemHeight: 10,
      },
      series: [{
        type: 'pie',
        radius: ['40%', '70%'],
        center: ['50%', '45%'],
        itemStyle: { borderRadius: 6, borderColor: 'rgba(15,23,42,0.8)', borderWidth: 2 },
        label: { show: false },
        emphasis: { label: { show: true, fontSize: 12, fontWeight: 700, color: T.text } },
        data,
      }],
    };
  }, [analytics]);

  // ── A/B test data (must be before ALL early returns per rules-of-hooks) ─
  const abTests = useMemo(() => {
    if (!analytics?.abTests && !analytics?.abComparison) return [];
    return analytics.abTests || analytics.abComparison || [];
  }, [analytics]);

  // ── Render: Loading state ─────────────────────────────────────────────

  if (loading && !campaign) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Spin size="large" tip="Loading campaign..." />
      </div>
    );
  }

  if (!campaign && !loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 16 }}>
        <Empty description={<span style={{ color: T.textMuted }}>Campaign not found</span>} />
        <button className="cd-btn-ghost" onClick={() => navigate(-1)}>
          <FontAwesomeIcon icon={faArrowLeft} /> Go Back
        </button>
      </div>
    );
  }

  const statusInfo = STATUS_CONFIG[campaign?.status] || STATUS_CONFIG.draft;
  const isRunning = campaign?.status === 'active';
  const isPaused = campaign?.status === 'paused';
  const isStopped = campaign?.status === 'cancelled' || campaign?.status === 'completed';

  // ═════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═════════════════════════════════════════════════════════════════════════

  return (
    <div style={{ padding: '24px 28px', minHeight: '100vh', background: T.pageBg }}>
      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <button
            className="cd-btn-ghost"
            onClick={() => navigate(-1)}
            style={{ padding: '8px 12px', minWidth: 'auto' }}
            title="Back to campaigns"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
              <h1 style={{ fontSize: 24, fontWeight: 800, color: T.text, margin: 0, letterSpacing: '-0.5px' }}>
                {campaign?.name || 'Untitled Campaign'}
              </h1>
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 12px',
                borderRadius: 20,
                fontSize: 12,
                fontWeight: 700,
                color: statusInfo.color,
                background: statusInfo.bg,
                textTransform: 'capitalize',
              }}>
                {isRunning && (
                  <span style={{
                    width: 7, height: 7, borderRadius: '50%',
                    background: T.emerald,
                    boxShadow: `0 0 0 3px ${T.emeraldGlow}`,
                    animation: 'cd-pulseGlow 2s ease-in-out infinite',
                  }} />
                )}
                {statusInfo.label}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, fontSize: 13, color: T.textMuted }}>
              <span>
                By: <strong style={{ color: T.textSecondary }}>
                  {campaign?.createdByName || 'Unknown'}
                </strong>
              </span>
              <span style={{ color: T.border }}>|</span>
              <span>
                Started: <strong style={{ color: T.textSecondary }}>
                  {campaign?.startedAt
                    ? dayjs(campaign.startedAt).format('MMM DD, YYYY h:mm A')
                    : campaign?.createdAt
                      ? dayjs(campaign.createdAt).format('MMM DD, YYYY')
                      : 'Not started'}
                </strong>
              </span>
              {campaign?.metrics?.totalRecipients != null && (
                <>
                  <span style={{ color: T.border }}>|</span>
                  <span>
                    <FontAwesomeIcon icon={faUsers} style={{ marginRight: 5 }} />
                    {campaign.metrics.totalRecipients} recipients
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* ── Action Buttons ──────────────────────────────────────────────── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {isRunning && (
            <>
              <button className="cd-btn-warning" onClick={() => handleStatusChange('paused')}>
                <FontAwesomeIcon icon={faPause} /> Pause
              </button>
              <button className="cd-btn-danger" onClick={() => handleStatusChange('cancelled')}>
                <FontAwesomeIcon icon={faStop} /> Stop
              </button>
            </>
          )}
          {isPaused && (
            <>
              <button className="cd-btn-primary" onClick={() => handleStatusChange('resumed')}>
                <FontAwesomeIcon icon={faPlay} /> Resume
              </button>
              <button className="cd-btn-danger" onClick={() => handleStatusChange('cancelled')}>
                <FontAwesomeIcon icon={faStop} /> Stop
              </button>
            </>
          )}
          {isStopped && campaign?.status !== 'completed' && (
            <button className="cd-btn-ghost" disabled style={{ opacity: 0.5 }}>
              <FontAwesomeIcon icon={faStop} /> Cancelled
            </button>
          )}
          {campaign?.status === 'draft' && (
            <button className="cd-btn-primary" onClick={() => handleStatusChange('active')}>
              <FontAwesomeIcon icon={faRocket} /> Launch
            </button>
          )}
          {campaign?.status === 'scheduled' && (
            <button className="cd-btn-warning" onClick={() => handleStatusChange('cancelled')}>
              <FontAwesomeIcon icon={faPause} /> Cancel Schedule
            </button>
          )}
        </div>
      </div>

      {/* ── Error display ─────────────────────────────────────────────────── */}
      {error && (
        <div style={{
          background: T.roseGlow,
          border: `1px solid ${T.rose}40`,
          borderRadius: T.radiusSm,
          padding: '12px 20px',
          marginBottom: 20,
          color: T.rose,
          fontSize: 13,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}>
          <FontAwesomeIcon icon={faTriangleExclamation} />
          <span>{typeof error === 'string' ? error : 'An error occurred'}</span>
        </div>
      )}

      {/* ── Tabs ──────────────────────────────────────────────────────────── */}
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        className="cd-tabs"
        destroyInactiveTabPane={false}
        items={[
          {
            key: 'analytics',
            label: (
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FontAwesomeIcon icon={faChartLine} /> Analytics
              </span>
            ),
            children: renderAnalyticsTab(),
          },
          {
            key: 'recipients',
            label: (
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FontAwesomeIcon icon={faUsers} />
                Recipients
                {recipientsTotal > 0 && (
                  <Badge
                    count={recipientsTotal}
                    showZero={false}
                    style={{ background: T.indigo, boxShadow: 'none', fontSize: 10, marginLeft: 2 }}
                  />
                )}
              </span>
            ),
            children: renderRecipientsTab(),
          },
          {
            key: 'livefeed',
            label: (
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FontAwesomeIcon icon={faWifi} /> Live Feed
                {isRunning && (
                  <span style={{
                    width: 7, height: 7, borderRadius: '50%',
                    background: T.emerald,
                    animation: 'cd-pulseGlow 2s ease-in-out infinite',
                  }} />
                )}
              </span>
            ),
            children: renderLiveFeedTab(),
          },
          {
            key: 'insights',
            label: (
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FontAwesomeIcon icon={faLightbulb} /> AI Insights
              </span>
            ),
            children: renderInsightsTab(),
          },
          {
            key: 'activity',
            label: (
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FontAwesomeIcon icon={faClockRotateLeft} /> Activity Log
              </span>
            ),
            children: renderActivityTab(),
          },
        ]}
      />

      {/* ── Status Change Confirmation Modal ─────────────────────────────── */}
      <Modal
        title={null}
        open={statusModalVisible}
        onCancel={() => { setStatusModalVisible(false); setPendingStatus(null); }}
        footer={null}
        centered
        width={420}
        bodyStyle={{ padding: 0 }}
        closable={false}
        maskStyle={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
      >
        <div style={{ padding: '28px 32px', background: T.cardBg, borderRadius: T.radius }}>
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              background: pendingStatus === 'cancelled'
                ? T.roseGlow
                : pendingStatus === 'paused'
                  ? T.amberGlow
                  : T.emeraldGlow,
              marginBottom: 16,
            }}>
              <FontAwesomeIcon
                icon={pendingStatus === 'cancelled' ? faStop : pendingStatus === 'paused' ? faPause : faPlay}
                style={{
                  fontSize: 22,
                  color: pendingStatus === 'cancelled'
                    ? T.rose
                    : pendingStatus === 'paused'
                      ? T.amber
                      : T.emerald,
                }}
              />
            </div>
            <h3 style={{ color: T.text, fontSize: 18, fontWeight: 700, margin: '0 0 8px' }}>
              {pendingStatus === 'cancelled' && 'Stop Campaign?'}
              {pendingStatus === 'paused' && 'Pause Campaign?'}
              {pendingStatus === 'resumed' && 'Resume Campaign?'}
              {pendingStatus === 'active' && 'Launch Campaign?'}
              {!['cancelled', 'paused', 'resumed', 'active'].includes(pendingStatus) && `Change to ${pendingStatus}?`}
            </h3>
            <p style={{ color: T.textMuted, fontSize: 13, margin: 0, lineHeight: 1.6 }}>
              {pendingStatus === 'cancelled' && 'This will permanently stop the campaign. Remaining emails will not be sent. This action cannot be undone.'}
              {pendingStatus === 'paused' && 'The campaign will be paused. No emails will be sent until resumed. You can resume at any time.'}
              {pendingStatus === 'resumed' && 'The campaign will resume sending from where it left off.'}
              {pendingStatus === 'active' && 'The campaign will start sending immediately per your drip schedule.'}
              {!['cancelled', 'paused', 'resumed', 'active'].includes(pendingStatus) && `Campaign status will be changed to "${pendingStatus}".`}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            <button
              className="cd-btn-ghost"
              onClick={() => { setStatusModalVisible(false); setPendingStatus(null); }}
            >
              Cancel
            </button>
            <button
              className={pendingStatus === 'cancelled' ? 'cd-btn-danger' : pendingStatus === 'paused' ? 'cd-btn-warning' : 'cd-btn-primary'}
              onClick={confirmStatusChange}
            >
              {pendingStatus === 'cancelled' ? 'Stop Campaign' : pendingStatus === 'paused' ? 'Pause' : pendingStatus === 'resumed' ? 'Resume' : 'Launch'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );

  // ═════════════════════════════════════════════════════════════════════════
  // TAB RENDER FUNCTIONS
  // ═════════════════════════════════════════════════════════════════════════

  // ── Tab 1: Analytics ──────────────────────────────────────────────────

  function renderAnalyticsTab() {
    if (analyticsLoading && !analytics) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <Spin size="large" tip="Loading analytics..." />
        </div>
      );
    }

    if (!analytics) {
      return <Empty description={<span style={{ color: T.textMuted }}>No analytics data available yet</span>} />;
    }

    return (
      <div>
        {/* ── Metric Cards ──────────────────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 16, marginBottom: 28 }}>
          {METRIC_DEFS.map((m, idx) => {
            const data = metrics?.[m.key] || {};
            return (
              <MetricCard
                key={m.key}
                label={m.label}
                value={data.value}
                percent={data.percent}
                sub={data.sub || (m.key === 'sent' ? `${campaign?.totalRecipients || 0} total recipients` : undefined)}
                color={m.color}
                gradient={m.gradient}
                icon={m.icon}
                delay={idx * 60}
              />
            );
          })}
        </div>

        {/* ── Funnel Chart ──────────────────────────────────────────────── */}
        <SectionHeader title="Conversion Funnel" icon={faFilter} />
        <div className="cd-glass-card" style={{ padding: 24, marginBottom: 24 }}>
          <FunnelChart
            data={[
              { name: 'Sent', value: metrics?.sent?.value || 0, color: T.indigo },
              { name: 'Delivered', value: metrics?.delivered?.value || 0, color: T.teal },
              { name: 'Opened', value: metrics?.opened?.value || 0, color: T.sky },
              { name: 'Clicked', value: metrics?.clicked?.value || 0, color: T.amber },
              { name: 'Replied', value: metrics?.replied?.value || 0, color: T.emerald },
            ]}
          />
        </div>

        {/* ── Timeline Chart ────────────────────────────────────────────── */}
        {timelineChartOption && (
          <>
            <SectionHeader title="Daily Performance" icon={faCalendarDay} />
            <div className="cd-glass-card" style={{ padding: 24, marginBottom: 24 }}>
              <ReactECharts option={timelineChartOption} style={{ height: 320 }} notMerge />
            </div>
          </>
        )}

        {/* ── Step Comparison + Heatmap Row ─────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
          {/* Per-step comparison */}
          <div>
            <SectionHeader title="Step Performance" icon={faArrowTrendUp} />
            <div className="cd-glass-card" style={{ padding: 24, height: 'calc(100% - 48px)' }}>
              {stepComparisonOption ? (
                <ReactECharts option={stepComparisonOption} style={{ height: 300 }} notMerge />
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
                  <Empty description={<span style={{ color: T.textMuted }}>No step data</span>} />
                </div>
              )}
            </div>
          </div>

          {/* Heatmap */}
          <div>
            <SectionHeader title="Opens by Hour & Day" icon={faClock} />
            <div className="cd-glass-card" style={{ padding: 24, height: 'calc(100% - 48px)' }}>
              {heatmapOption ? (
                <ReactECharts option={heatmapOption} style={{ height: 300 }} notMerge />
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
                  <Empty description={<span style={{ color: T.textMuted }}>No heatmap data</span>} />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── A/B Comparison Cards ──────────────────────────────────────── */}
        {abTests.length > 0 && (
          <>
            <SectionHeader title="A/B Test Results" icon={faBolt} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 16, marginBottom: 24 }}>
              {abTests.map((ab, idx) => (
                <div key={idx} className="cd-ab-card" style={{ animationDelay: `${idx * 80}ms` }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 14 }}>
                    {ab.stepName || `Step ${ab.step || idx + 1}`}
                    {ab.winner && (
                      <Tag color="green" style={{ marginLeft: 10, fontSize: 10 }}>
                        Winner: {ab.winner}
                      </Tag>
                    )}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    {/* Variant A */}
                    <div style={{
                      padding: 14, borderRadius: 10,
                      background: ab.winner === 'A' ? `${T.emerald}12` : 'rgba(255,255,255,0.04)',
                      border: `1px solid ${ab.winner === 'A' ? `${T.emerald}30` : T.border}`,
                    }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: T.indigo, marginBottom: 8 }}>Variant A</div>
                      <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 4 }}>
                        Subject: <span style={{ color: T.textSecondary }}>{ab.variantA?.subject || ab.subjectA || '-'}</span>
                      </div>
                      <div style={{ display: 'flex', gap: 14, marginTop: 10 }}>
                        <div>
                          <div style={{ fontSize: 10, color: T.textMuted }}>Open%</div>
                          <div style={{ fontSize: 18, fontWeight: 800, color: T.sky }}>
                            {ab.variantA?.openRate ?? ab.openRateA ?? '-'}%
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: 10, color: T.textMuted }}>Reply%</div>
                          <div style={{ fontSize: 18, fontWeight: 800, color: T.emerald }}>
                            {ab.variantA?.replyRate ?? ab.replyRateA ?? '-'}%
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: 10, color: T.textMuted }}>Click%</div>
                          <div style={{ fontSize: 18, fontWeight: 800, color: T.amber }}>
                            {ab.variantA?.clickRate ?? ab.clickRateA ?? '-'}%
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Variant B */}
                    <div style={{
                      padding: 14, borderRadius: 10,
                      background: ab.winner === 'B' ? `${T.emerald}12` : 'rgba(255,255,255,0.04)',
                      border: `1px solid ${ab.winner === 'B' ? `${T.emerald}30` : T.border}`,
                    }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: T.violet, marginBottom: 8 }}>Variant B</div>
                      <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 4 }}>
                        Subject: <span style={{ color: T.textSecondary }}>{ab.variantB?.subject || ab.subjectB || '-'}</span>
                      </div>
                      <div style={{ display: 'flex', gap: 14, marginTop: 10 }}>
                        <div>
                          <div style={{ fontSize: 10, color: T.textMuted }}>Open%</div>
                          <div style={{ fontSize: 18, fontWeight: 800, color: T.sky }}>
                            {ab.variantB?.openRate ?? ab.openRateB ?? '-'}%
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: 10, color: T.textMuted }}>Reply%</div>
                          <div style={{ fontSize: 18, fontWeight: 800, color: T.emerald }}>
                            {ab.variantB?.replyRate ?? ab.replyRateB ?? '-'}%
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: 10, color: T.textMuted }}>Click%</div>
                          <div style={{ fontSize: 18, fontWeight: 800, color: T.amber }}>
                            {ab.variantB?.clickRate ?? ab.clickRateB ?? '-'}%
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ── Timezone + Engagement Row ─────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          <div>
            <SectionHeader title="Timezone Distribution" icon={faGlobe} />
            <div className="cd-glass-card" style={{ padding: 24 }}>
              {timezoneChartOption ? (
                <ReactECharts option={timezoneChartOption} style={{ height: 280 }} notMerge />
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 280 }}>
                  <Empty description={<span style={{ color: T.textMuted }}>No timezone data</span>} />
                </div>
              )}
            </div>
          </div>

          <div>
            <SectionHeader title="Engagement Distribution" icon={faFire} />
            <div className="cd-glass-card" style={{ padding: 24 }}>
              {engagementChartOption ? (
                <ReactECharts option={engagementChartOption} style={{ height: 280 }} notMerge />
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 280 }}>
                  <Empty description={<span style={{ color: T.textMuted }}>No engagement data</span>} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Tab 2: Recipients ─────────────────────────────────────────────────

  function renderRecipientsTab() {
    return (
      <div>
        {/* Status filter tabs */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
          <div className="cd-status-filter" style={{ marginBottom: 0 }}>
            {RECIPIENT_STATUSES.map((s) => (
              <button
                key={s.key}
                className={`cd-status-btn ${recipientFilter === s.key ? 'active' : ''}`}
                onClick={() => { setRecipientFilter(s.key); setRecipientPage(1); }}
              >
                {s.label}
              </button>
            ))}
          </div>
          <Input.Search
            placeholder="Search recipients..."
            className="cd-search-input"
            style={{ width: 260 }}
            allowClear
            value={recipientSearch}
            onChange={(e) => setRecipientSearch(e.target.value)}
            onSearch={(val) => { setRecipientSearch(val); setRecipientPage(1); }}
          />
        </div>

        {/* Recipient Table */}
        {recipientsLoading && !recipients.length ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
            <Spin size="large" />
          </div>
        ) : (
          <RecipientTable
            recipients={recipients}
            steps={campaign?.steps || []}
            total={recipientsTotal}
            loading={recipientsLoading}
            page={recipientPage}
            pageSize={50}
            onPageChange={(page) => setRecipientPage(page)}
            campaignId={id}
            onStatusChange={(recipientId, status) => {
              dispatch(changeRecipientStatus({ campaignId: id, recipientId, status }))
                .unwrap()
                .then(() => message.success('Recipient status updated'))
                .catch((err) => message.error(err || 'Failed to update status'));
            }}
          />
        )}
      </div>
    );
  }

  // ── Tab 3: Live Feed ──────────────────────────────────────────────────

  function renderLiveFeedTab() {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ color: T.text, fontSize: 14, fontWeight: 700 }}>Real-Time Events</span>
            {isRunning && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '3px 10px', borderRadius: 20,
                background: T.emeraldGlow, color: T.emerald,
                fontSize: 11, fontWeight: 600,
              }}>
                <span style={{
                  width: 6, height: 6, borderRadius: '50%',
                  background: T.emerald,
                  animation: 'cd-pulseGlow 2s ease-in-out infinite',
                }} />
                Live
              </span>
            )}
          </div>
          <button
            className="cd-btn-ghost"
            style={{ padding: '6px 14px', fontSize: 12 }}
            onClick={() => {
              setFeedLoading(true);
              dispatch(fetchLiveFeed({ campaignId: id, limit: 50 }))
                .finally(() => setFeedLoading(false));
            }}
          >
            <FontAwesomeIcon icon={faRefresh} /> Refresh
          </button>
        </div>

        {feedLoading && !liveFeed.length ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
            <Spin size="large" />
          </div>
        ) : liveFeed.length === 0 ? (
          <Empty description={<span style={{ color: T.textMuted }}>No events yet. Events will appear here as the campaign runs.</span>} />
        ) : (
          <LiveFeed
            events={liveFeed}
            eventIcons={EVENT_ICONS}
          />
        )}
      </div>
    );
  }

  // ── Tab 4: AI Insights ────────────────────────────────────────────────

  function renderInsightsTab() {
    if (insightsLoading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <Spin size="large" tip="Generating insights..." />
        </div>
      );
    }

    if (!insights || insights.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <Empty
            description={<span style={{ color: T.textMuted }}>No insights available yet. Insights are generated after sufficient campaign data is collected.</span>}
          />
          <button
            className="cd-btn-primary"
            style={{ marginTop: 20 }}
            onClick={() => {
              setInsightsLoading(true);
              dispatch(fetchInsights(id)).finally(() => setInsightsLoading(false));
            }}
          >
            <FontAwesomeIcon icon={faLightbulb} /> Generate Insights
          </button>
        </div>
      );
    }

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <span style={{ color: T.text, fontSize: 14, fontWeight: 700 }}>
            <FontAwesomeIcon icon={faLightbulb} style={{ color: T.amber, marginRight: 8 }} />
            AI-Powered Recommendations
          </span>
          <button
            className="cd-btn-ghost"
            style={{ padding: '6px 14px', fontSize: 12 }}
            onClick={() => {
              setInsightsLoading(true);
              dispatch(fetchInsights(id)).finally(() => setInsightsLoading(false));
            }}
          >
            <FontAwesomeIcon icon={faRefresh} /> Refresh
          </button>
        </div>

        {insights.map((insight, idx) => {
          const insightText = typeof insight === 'string' ? insight : insight?.text || insight?.message || insight?.recommendation || '';
          const insightType = typeof insight === 'object' ? (insight?.type || insight?.category || 'general') : 'general';
          const insightPriority = typeof insight === 'object' ? (insight?.priority || insight?.severity || 'medium') : 'medium';

          const priorityColors = {
            high: T.rose,
            critical: T.rose,
            medium: T.amber,
            low: T.emerald,
            info: T.sky,
          };

          return (
            <div
              key={idx}
              className="cd-insight-card"
              style={{ animationDelay: `${idx * 80}ms` }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                <div style={{
                  width: 38, height: 38, borderRadius: 10,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: T.amberGlow, flexShrink: 0,
                }}>
                  <span style={{ fontSize: 18 }}>💡</span>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    {insightType !== 'general' && (
                      <Tag
                        style={{
                          background: 'rgba(99,102,241,0.1)',
                          border: 'none',
                          color: T.indigo,
                          fontSize: 10,
                          fontWeight: 600,
                          textTransform: 'capitalize',
                          borderRadius: 6,
                        }}
                      >
                        {insightType}
                      </Tag>
                    )}
                    {insightPriority !== 'medium' && (
                      <span style={{
                        fontSize: 10, fontWeight: 700,
                        color: priorityColors[insightPriority] || T.textMuted,
                        textTransform: 'uppercase',
                      }}>
                        {insightPriority}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 13, color: T.text, lineHeight: 1.7 }}>
                    {insightText}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // ── Tab 5: Activity Log ───────────────────────────────────────────────

  function renderActivityTab() {
    if (activityLoading && !activityLog.length) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <Spin size="large" tip="Loading activity log..." />
        </div>
      );
    }

    if (!activityLog || activityLog.length === 0) {
      return <Empty description={<span style={{ color: T.textMuted }}>No activity recorded yet</span>} />;
    }

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <span style={{ color: T.text, fontSize: 14, fontWeight: 700 }}>
            <FontAwesomeIcon icon={faClockRotateLeft} style={{ color: T.indigo, marginRight: 8 }} />
            Campaign Activity
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 12, color: T.textMuted }}>
              {activityLogTotal} total events
            </span>
            <button
              className="cd-btn-ghost"
              style={{ padding: '6px 14px', fontSize: 12 }}
              onClick={() => {
                setActivityLoading(true);
                dispatch(fetchActivityLog({ campaignId: id, page: activityPage, limit: 50 }))
                  .finally(() => setActivityLoading(false));
              }}
            >
              <FontAwesomeIcon icon={faRefresh} /> Refresh
            </button>
          </div>
        </div>

        <ActivityTimeline
          activities={activityLog}
          loading={activityLoading}
        />

        {/* Pagination */}
        {activityLogTotal > 50 && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 24, gap: 8 }}>
            <button
              className="cd-btn-ghost"
              style={{ padding: '6px 14px', fontSize: 12 }}
              disabled={activityPage <= 1}
              onClick={() => setActivityPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <span style={{
              display: 'inline-flex', alignItems: 'center',
              padding: '6px 14px', fontSize: 12, color: T.textSecondary,
            }}>
              Page {activityPage} of {Math.ceil(activityLogTotal / 50)}
            </span>
            <button
              className="cd-btn-ghost"
              style={{ padding: '6px 14px', fontSize: 12 }}
              disabled={activityPage >= Math.ceil(activityLogTotal / 50)}
              onClick={() => setActivityPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>
    );
  }
};

export default CampaignDetail;
